import { Module } from "vuex";
import { IInstallment, IInstallmentDetail } from "@/types/IInstallment";
import { ISuperPacmanInstallment } from "@/types/ISuperPacman";
import { IDeviceInfoState, IPayOptionsState } from "@/types/IPayOptionsState";
import { IWompiAcceptanceTokenResponse } from "@/types/IWompiResponse";

const state: IPayOptionsState = {
  user: {
    token: null,
    locale: null,
    showHighlight: true,
  },
  deviceInfo: {
    deviceTag: null,
    country: null,
    currencySymbol: null,
    phoneNumber: null,
    paymentOption: null,
  },
  selectedInstallment: null,
  superPacmanInstallment: null,
  superPacmanSelectedAmount: null,
  installments: null,
  error: false,
};

const mutations = {
  setSuperPacmanInstallment(state: IPayOptionsState, installment: ISuperPacmanInstallment) {
    state.superPacmanInstallment = installment;
  },
  setSuperPacmanAmount(state: IPayOptionsState, amount: number) {
    state.superPacmanSelectedAmount = amount;
  },
  setInstallments(state: IPayOptionsState, installments: IInstallment[]) {
    state.installments = installments;
  },
  setError(state: IPayOptionsState, error: boolean) {
    state.error = error;
  },
  setUserToken(state: IPayOptionsState, token: string) {
    state.user.token = token;
  },
  setUserLocale(state: IPayOptionsState, locale: string) {
    state.user.locale = locale;
  },
  setShowHighlight(state: IPayOptionsState, showHighlight: boolean) {
    state.user.showHighlight = showHighlight;
  },
  setDeviceInfo(state: IPayOptionsState, deviceInfo: Partial<IDeviceInfoState>) {
    state.deviceInfo = { ...state.deviceInfo, ...deviceInfo };
  },
  setSelectedInstallment(state: IPayOptionsState, installment: IInstallmentDetail | null) {
    state.selectedInstallment = installment;
  },
  setDeviceTag(state: IPayOptionsState, deviceTag: string) {
    state.deviceInfo.deviceTag = deviceTag;
  },
  setPaymentOption(state: IPayOptionsState, paymentOption: string) {
    state.deviceInfo.paymentOption = paymentOption;
  },
  setCountry(state: IPayOptionsState, country: string) {
    state.deviceInfo.country = country;
  },
  setCurrencySymbol(state: IPayOptionsState, currencySymbol: string) {
    state.deviceInfo.currencySymbol = currencySymbol;
  },
  setPhoneNumber(state: IPayOptionsState, phoneNumber: string) {
    state.deviceInfo.phoneNumber = phoneNumber;
  },
  setWompiAcceptanceToken(state: IPayOptionsState, token: IWompiAcceptanceTokenResponse | null) {
    if (state.selectedInstallment) {
      state.selectedInstallment.acceptanceToken = token ?? undefined;
    }
  }
};

const actions = {
  setSuperPacmanInstallment({ commit }, installment: ISuperPacmanInstallment) {
    commit("setSuperPacmanInstallment", installment);
  },
  setSuperPacmanAmount({ commit }, amount: number) {
    commit("setSuperPacmanAmount", amount);
  },
  setInstallments({ commit }, installments: IInstallment[]) {
    commit("setInstallments", installments);
  },
  setError({ commit }, error: boolean) {
    commit("setError", error);
  },
  setUserToken({ commit }, token: string) {
    commit("setUserToken", token);
  },
  setDeviceTag({ commit }, deviceTag: string) {
    commit("setDeviceTag", deviceTag);
  },
  setPaymentOption({ commit }, paymentOption: string) {
    commit("setPaymentOption", paymentOption);
  },
  setCountry({ commit }, country: string) {
    commit("setCountry", country);
  },
  setSelectedInstallment({ commit }, installment: IInstallmentDetail | null) {
    commit("setSelectedInstallment", installment);
  },
  setCurrencySymbol({ commit }, currencySymbol: string) {
    commit("setCurrencySymbol", currencySymbol);
  },
  setPhoneNumber({ commit }, phoneNumber: string) {
    commit("setPhoneNumber", phoneNumber);
  },
  setWompiAcceptanceToken({ commit }, token: IWompiAcceptanceTokenResponse | null) {
    commit("setWompiAcceptanceToken", token);
  },
  determineOption({ dispatch }, showOptions: string | undefined) {
    let option = "option_a";
    if (showOptions === "1" || showOptions === "option_a") {
      option = "option_a";
    } else if (showOptions === "2" || showOptions === "option_b") {
      option = "option_b";
    }
    dispatch("setPaymentOption", option);
    return option;
  },
};

const getters = {
  getSuperPacmanInstallment(state: IPayOptionsState): ISuperPacmanInstallment | null {
    return state.superPacmanInstallment;
  },
  getSuperPacmanAmount(state: IPayOptionsState): number | null {
    return state.superPacmanSelectedAmount;
  },
  getInstallments(state: IPayOptionsState): IInstallment[] | null {
    return state.installments;
  },
  hasError(state: IPayOptionsState): boolean {
    return state.error;
  },
  getUserToken(state: IPayOptionsState): string | null {
    return state.user.token;
  },
  getUserLocale(state: IPayOptionsState): string | null {
    return state.user.locale;
  },
  getShowHighlight(state: IPayOptionsState): boolean {
    return state.user.showHighlight;
  },
  getDeviceInfo(state: IPayOptionsState): IDeviceInfoState {
    return state.deviceInfo;
  },
  getSelectedInstallment(state: IPayOptionsState): IInstallmentDetail | null {
    return state.selectedInstallment;
  },
};

const payOptionsStore: Module<IPayOptionsState, any> = {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};

export default payOptionsStore;
